"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import Link from "next/link";
import "./business-knowledge.css";

/* Images — public/assets/images/ se serve hoti hain */
const IMG = "/assets/images";
const img = (name: string) => encodeURI(`${IMG}/${name}`);
const imgHero = img("business-knowledge-hero.jpg");
const imgSubscribe = img("lets-banner.jpg");

/* All styling lives in business-knowledge.css (no styling in this file). */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400) =>
  ({ "--kh-delay": `${Math.min(i * step, max)}ms` } as React.CSSProperties);

/* ---------- Icons ---------- */

type IconKey =
  | "layers" | "chart" | "gear" | "db" | "nodes" | "trend" | "doc" | "dots"
  | "book" | "bulb" | "frame" | "chat" | "learn" | "apply" | "solve" | "plus";

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    layers: (<><path d="m12 3 9 5-9 5-9-5 9-5Z" /><path d="m3 12.5 9 5 9-5M3 16.5l9 5 9-5" /></>),
    chart: <path d="M5 20V12M11 20V4M17 20V9M3 20h18" />,
    gear: (<><circle cx="12" cy="12" r="3" /><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" /></>),
    db: (<><ellipse cx="12" cy="5.5" rx="7" ry="2.8" /><path d="M5 5.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6M5 11.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6" /></>),
    nodes: (<><circle cx="6" cy="6" r="2.2" /><circle cx="18" cy="6" r="2.2" /><circle cx="6" cy="18" r="2.2" /><circle cx="18" cy="18" r="2.2" /><circle cx="12" cy="12" r="2.4" /><path d="m7.6 7.6 2.7 2.7M16.4 7.6l-2.7 2.7M7.6 16.4l2.7-2.7M16.4 16.4l-2.7-2.7" /></>),
    trend: (<><path d="M3 17l5-5 4 4 8-9" /><path d="M15 7h5v5" /></>),
    doc: (<><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 9h6M9 13h6M9 17h3" /></>),
    dots: (<><circle cx="5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="19" cy="12" r="1.4" /></>),
    book: (<><path d="M12 6c-2-1.5-5-2-8-2v14c3 0 6 .5 8 2 2-1.5 5-2 8-2V4c-3 0-6 .5-8 2Z" /><path d="M12 6v14" /></>),
    bulb: (<><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3Z" /></>),
    frame: (<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M9 9v11" /></>),
    chat: (<><path d="M4 5h16v11H9l-5 4V5Z" /><path d="M8 9.5h8M8 12.5h5" /></>),
    learn: (<><path d="M12 6c-2-1.5-5-2-8-2v14c3 0 6 .5 8 2 2-1.5 5-2 8-2V4c-3 0-6 .5-8 2Z" /><path d="M12 6v14" /></>),
    apply: (<><circle cx="12" cy="12" r="3" /><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" /></>),
    solve: (<><path d="M5 20V12M11 20V4M17 20V9M3 20h18" /></>),
    plus: <path d="M12 5v14M5 12h14" />,
  };

  return (
    <svg
      className="kh-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const ALL = "All Topics";

const topics: { icon: IconKey; label: string }[] = [
  { icon: "layers", label: ALL },
  { icon: "chart", label: "Data & Analytics" },
  { icon: "gear", label: "AI & GenAI" },
  { icon: "db", label: "SAP & Enterprise Systems" },
  { icon: "nodes", label: "Operations & Supply Chain" },
  { icon: "trend", label: "Finance & Risk" },
  { icon: "doc", label: "People & Change" },
];

const steps: { icon: IconKey; title: string; text: string }[] = [
  { icon: "learn", title: "Learn", text: "Understand key concepts and business terminologies." },
  { icon: "apply", title: "Apply", text: "Use frameworks and how-to guides in your context." },
  { icon: "solve", title: "Solve", text: "Gain practical insights to make better decisions." },
];

type Article = {
  type: string;
  topic: string;
  title: string;
  text: string;
  read: string;
  date: string;
  image: string;
  href: string;
};

const articles: Article[] = [
  { type: "Explainer", topic: "Data & Analytics", title: "What is a data product?", text: "A simple guide to understanding, building and operationalising data products.", read: "6 min read", date: "15 Sep 2026", image: img("Data-and-Intelligence.jpg"), href: "/knowledge/what-is-a-data-product" },
  { type: "Framework", topic: "AI & GenAI", title: "The AI adoption framework for enterprises", text: "A step-by-step approach to move from experimentation to scaled value.", read: "8 min read", date: "12 Sep 2026", image: img("enterprise-ai-map.jpg"), href: "/knowledge/ai-adoption-framework" },
  { type: "Perspective", topic: "Data & Analytics", title: "Key data management principles every business should follow", text: "Core principles to build trusted, AI-ready enterprise data.", read: "5 min read", date: "09 Sep 2026", image: img("Data-Driven-Decisions.jpg"), href: "/knowledge/data-management-principles" },
  { type: "How-to guide", topic: "SAP & Enterprise Systems", title: "Understanding SAP data in the age of AI", text: "Key data objects, integration patterns and opportunities for analytics and AI.", read: "7 min read", date: "05 Sep 2026", image: img("sap bdc for the ai era.png"), href: "/knowledge/sap-data-in-the-age-of-ai" },
  { type: "Explainer", topic: "Operations & Supply Chain", title: "Why inventory visibility breaks before it scales", text: "Where stock data goes wrong across plants, partners and systems.", read: "6 min read", date: "01 Sep 2026", image: img("Inventory_availability.jpg"), href: "/knowledge/inventory-visibility" },
  { type: "Perspective", topic: "Finance & Risk", title: "From reporting to liquidity insight", text: "How finance teams can see cash and working capital sooner.", read: "5 min read", date: "28 Aug 2026", image: img("Better-Liquidity.jpg"), href: "/knowledge/liquidity-insight" },
  { type: "How-to guide", topic: "People & Change", title: "Helping teams adopt new ways of working", text: "Practical steps to make change stick across business functions.", read: "7 min read", date: "24 Aug 2026", image: img("people-change-business.jpg"), href: "/knowledge/people-change-adoption" },
];

const glossaryTabs = ["Popular Terms", "Data & Analytics", "AI & GenAI", "SAP & ERP", "Operations"] as const;

const glossary: Record<(typeof glossaryTabs)[number], { term: string; def: string }[]> = {
  "Popular Terms": [
    { term: "Data Product", def: "A reusable, governed and value-driven data asset designed for a specific business outcome." },
    { term: "AI Agent", def: "An autonomous system that can perceive, reason, take actions and achieve goals with minimal human intervention." },
    { term: "Master Data", def: "Core, non-transactional data that provides a consistent view of key business entities." },
    { term: "Data Governance", def: "Policies, processes and controls to ensure data is accurate, secure, accessible and trusted." },
  ],
  "Data & Analytics": [
    { term: "Data Lineage", def: "A record of where data comes from, how it changes and where it is used." },
    { term: "Data Quality", def: "How accurate, complete and consistent data is for the way it is used." },
    { term: "KPI", def: "A measurable value that shows how well a business goal is being achieved." },
    { term: "Data Lake", def: "A central store that holds large volumes of raw data in its original format." },
  ],
  "AI & GenAI": [
    { term: "Large Language Model", def: "An AI model trained on large amounts of text to understand and generate language." },
    { term: "RAG", def: "A method that lets an AI model look up trusted company information before it answers." },
    { term: "Prompt", def: "The instruction or question given to an AI model to guide its response." },
    { term: "Hallucination", def: "When an AI model gives an answer that sounds confident but is not correct." },
  ],
  "SAP & ERP": [
    { term: "SAP S/4HANA", def: "SAP's next-generation ERP suite for running core business processes." },
    { term: "SAP BTP", def: "SAP's platform for building, integrating and extending business applications." },
    { term: "Business Data Cloud", def: "SAP's environment for bringing business data together for analytics and AI." },
    { term: "Clean Core", def: "Keeping the ERP standard and moving custom changes to extensions outside it." },
  ],
  Operations: [
    { term: "Safety Stock", def: "Extra inventory held to protect against demand or supply surprises." },
    { term: "Lead Time", def: "The time between placing an order and receiving it." },
    { term: "S&OP", def: "A regular process that aligns sales, supply and finance plans." },
    { term: "Working Capital", def: "The cash tied up in day-to-day operations such as stock and receivables." },
  ],
};

const formats: { icon: IconKey; title: string; text: string; href: string }[] = [
  { icon: "doc", title: "Articles", text: "In-depth insights on key topics and trends.", href: "/knowledge/articles" },
  { icon: "chat", title: "How-to Guides", text: "Step-by-step guidance for practical application.", href: "/knowledge/how-to-guides" },
  { icon: "frame", title: "Frameworks & Models", text: "Proven approaches to solve business challenges.", href: "/knowledge/frameworks" },
  { icon: "bulb", title: "Explainers", text: "Simple explanations for complex concepts.", href: "/knowledge/explainers" },
];

/* ============================== PAGE ============================== */

export default function KnowledgePage() {
  const ref = useScrollReveal();

  const [topic, setTopic] = useState<string>(ALL);
  const [tab, setTab] = useState<(typeof glossaryTabs)[number]>("Popular Terms");
  const [open, setOpen] = useState<string | null>("Data Product");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const visible = useMemo(
    () => (topic === ALL ? articles.slice(0, 4) : articles.filter((a) => a.topic === topic)),
    [topic]
  );
  const lead = visible[0];
  const rest = visible.slice(1);

  const onSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSent(true);
    setEmail("");
  };

  return (
    <div className="kh-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="kh-hero" id="top">
          <div className="kh-hero-bg" style={{ backgroundImage: `url("${imgHero}")` }} aria-hidden="true"></div>
          <div className="kh-hero-overlay" aria-hidden="true"></div>

          <div className="kh-hero-inner">
            <div className="kh-hero-copy">
              <h1 data-reveal style={delay(0)}>
                <span className="kh-line">Practical knowledge</span>
                <span className="kh-line">for <em>real business</em></span>
                <span className="kh-line"><em>challenges.</em></span>
              </h1>
              <p data-reveal style={delay(1, 120)}>
                Concepts, frameworks and expert insights to help you understand, solve and stay ahead —
                from data and AI to industry operations and transformation.
              </p>
              <div className="kh-actions" data-reveal style={delay(2, 120)}>
                <Link href="#featured" className="kh-btn kh-btn-primary">
                  Explore Knowledge Topics <span aria-hidden="true">→</span>
                </Link>
                {/* <Link href="#subscribe" className="kh-btn kh-btn-outline">
                  Subscribe for Updates <span aria-hidden="true">→</span>
                </Link> */}
              </div>
            </div>
          </div>
        </section>

        {/* ============================ TOPIC BAR ============================ */}
        <section className="kh-topicbar" aria-label="Knowledge topics">
          <div className="kh-container">
            <div className="kh-topics" role="tablist" data-reveal>
              {topics.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  role="tab"
                  aria-selected={topic === t.label}
                  className={`kh-topic${topic === t.label ? " is-active" : ""}`}
                  onClick={() => setTopic(t.label)}
                >
                  <Icon name={t.icon} />
                  <span>{t.label}</span>
                </button>
              ))}
              <Link href="/knowledge/topics" className="kh-topic kh-topic--more">
                <Icon name="dots" />
                <span>More Topics</span>
              </Link>
            </div>
          </div>
        </section>

        {/* ======================== CONCEPTS TO ACTION ======================= */}
        <section className="kh-section">
          <div className="kh-container">
            <div className="kh-flow">
              <div className="kh-flow-copy" data-reveal>
                <h2>From concepts to confident action.</h2>
                <p>
                  Our business knowledge hub brings together simple explanations, proven frameworks and
                  practical guidance to help you turn knowledge into impact.
                </p>
              </div>

              <div className="kh-flow-steps">
                <h3 data-reveal>How to use this section</h3>
                <ol className="kh-steps">
                  {steps.map((s, i) => (
                    <li className="kh-step" key={s.title} data-reveal style={delay(i, 90)}>
                      <span className="kh-step-no">{i + 1}</span>
                      <span className="kh-step-icon"><Icon name={s.icon} /></span>
                      <strong>{s.title}</strong>
                      <p>{s.text}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* ========================== FEATURED KNOWLEDGE ===================== */}
        <section className="kh-section" id="featured">
          <div className="kh-container">
            <div className="kh-head kh-head--row" data-reveal>
              <div>
                <h2>Featured knowledge</h2>
                <p>Essential reads to build your understanding.</p>
              </div>
              <Link href="/knowledge" className="kh-link">View all knowledge <span aria-hidden="true">→</span></Link>
            </div>

            {lead ? (
              <div className="kh-feature" key={topic}>
                <article className="kh-lead">
                  <Link href={lead.href} className="kh-lead-image">
                    <img src={lead.image} alt="" aria-hidden="true" loading="lazy" />
                  </Link>
                  <div className="kh-lead-body">
                    <span className="kh-tag">{lead.type}</span>
                    <h3><Link href={lead.href}>{lead.title}</Link></h3>
                    <p>{lead.text}</p>
                    <div className="kh-meta">
                      <span>{lead.read}</span>
                      <span>{lead.date}</span>
                    </div>
                  </div>
                </article>

                {rest.length > 0 && (
                  <ul className="kh-list">
                    {rest.map((a) => (
                      <li className="kh-row" key={a.title}>
                        <Link href={a.href} className="kh-row-image">
                          <img src={a.image} alt="" aria-hidden="true" loading="lazy" />
                        </Link>
                        <div className="kh-row-body">
                          <span className="kh-tag">{a.type}</span>
                          <h3><Link href={a.href}>{a.title}</Link></h3>
                          <p>{a.text}</p>
                          <div className="kh-meta">
                            <span>{a.read}</span>
                            <span>{a.date}</span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <p className="kh-empty">No articles in this topic yet. Pick another topic or view all knowledge.</p>
            )}
          </div>
        </section>

        {/* ============================== GLOSSARY ============================ */}
        <section className="kh-section kh-glossary-section">
          <div className="kh-container">
            <div className="kh-glossary">
              <div className="kh-glossary-copy" data-reveal>
                <h2>Business terms, broken down.</h2>
                <p>Clear and concise explanations of key business, data, AI and industry terms — without the jargon.</p>
                {/* <Link href="/knowledge/glossary" className="kh-btn kh-btn-primary">
                  Browse Glossary <span aria-hidden="true">→</span>
                </Link> */}
              </div>

              <div className="kh-glossary-panel" data-reveal style={delay(1, 100)}>
                <div className="kh-gtabs" role="tablist">
                  {glossaryTabs.map((g) => (
                    <button
                      key={g}
                      type="button"
                      role="tab"
                      aria-selected={tab === g}
                      className={`kh-gtab${tab === g ? " is-active" : ""}`}
                      onClick={() => {
                        setTab(g);
                        setOpen(glossary[g][0].term);
                      }}
                    >
                      {g}
                    </button>
                  ))}
                </div>

                <ul className="kh-terms">
                  {glossary[tab].map((g) => {
                    const isOpen = open === g.term;
                    return (
                      <li key={g.term} className={`kh-term${isOpen ? " is-open" : ""}`}>
                        <button
                          type="button"
                          className="kh-term-btn"
                          aria-expanded={isOpen}
                          onClick={() => setOpen(isOpen ? null : g.term)}
                        >
                          <span>{g.term}</span>
                          <span className="kh-term-plus"><Icon name="plus" /></span>
                        </button>
                        <div className="kh-term-def"><p>{g.def}</p></div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============================ BY FORMAT ============================ */}
        <section className="kh-section">
          <div className="kh-container">
            <div className="kh-head kh-head--row" data-reveal>
              <div>
                <h2>Knowledge by format</h2>
                <p>Choose how you want to learn.</p>
              </div>
              {/* <Link href="/knowledge/formats" className="kh-link">View all formats <span aria-hidden="true">→</span></Link> */}
            </div>

            <div className="kh-formats">
              {formats.map((f, i) => (
                <div className="kh-format" key={f.title} data-reveal style={delay(i, 80)}>
                  <span className="kh-format-icon"><Icon name={f.icon} /></span>
                  <strong>{f.title}</strong>
                  <span className="kh-format-text">{f.text}</span>
                  <span className="kh-format-go" aria-hidden="true">→</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ SUBSCRIBE ============================ */}
        <section className="kh-cta" id="subscribe">
          <img className="kh-cta-bg" src={imgSubscribe} alt="" aria-hidden="true" />
          <div className="kh-cta-copy">
            <h2 data-reveal>Get the latest business knowledge, straight to your inbox.</h2>
            <p data-reveal style={delay(1, 120)}>New insights, guides and frameworks — curated for you.</p>

            {/* <form className="kh-form" onSubmit={onSubscribe} data-reveal style={delay(2, 120)}>
              <label className="kh-sr" htmlFor="kh-email">Email address</label>
              <input
                id="kh-email"
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setSent(false);
                }}
                placeholder="Enter your email address"
                autoComplete="email"
              />
              <button type="submit" className="kh-btn kh-btn-primary">
                Subscribe <span aria-hidden="true">→</span>
              </button>
              <small role="status">{sent ? "Thanks — you're subscribed." : "No spam. Just practical knowledge."}</small>
            </form> */}
          </div>
        </section>
      </main>
    </div>
  );
}